<template>
  <div ref="mapContainer" class="world-map-container" style="position: relative; width: 100%; height: 100%;">
    <div
      v-if="tooltipPosition && (tooltipCountry || tooltipLocation)"
      class="tooltip"
      :style="{
        top: tooltipPosition.y - 12 + 'px',
        left: tooltipPosition.x + 12 + 'px',
        transform: `scale(${1 / zoomLevel})`,
        transformOrigin: 'top left'
      }"
    >
      <template v-if="tooltipCountry">
        <h4 class="tooltip__title">
          {{ tooltipCountry.name }} <span v-if="tooltipCountry.marked" class="has-text-weight-normal">({{ tooltipCountry.count.toLocaleString() }})</span>
        </h4>
      </template>

      <template v-if="tooltipLocation">
        <h4 v-if="tooltipLocation.country" class="tooltip__title">
          {{ tooltipLocation.country.name }}
        </h4>
        <h5 class="tooltip__subtitle">
          {{ tooltipLocation.longitude }}, {{ tooltipLocation.latitude }}
        </h5>
        <ul v-if="tooltipLocation.items && tooltipLocation.items.length">
          <li v-for="item in tooltipLocation.items" :key="item">
            {{ item }}
          </li>
        </ul>
      </template>
    </div>

    <svg
      ref="mapSvg"
      :viewBox="`0 0 ${actualWidth} ${actualHeight}`"
      class="world-map"
      :style="{ width: '100%', height: '100%' }"
      preserveAspectRatio="xMidYMid meet"
    >
      <defs>
        <pattern
          id="smallGrid" class="pattern"
          width="5" height="5" patternUnits="userSpaceOnUse"
        >
          <path
            d="M 5 0 L 0 0 0 5" fill="none" stroke-width="0.5"
          />
        </pattern>
        <pattern
          id="grid" class="pattern"
          width="20" height="20" patternUnits="userSpaceOnUse"
        >
          <rect width="20" height="20" fill="url(#smallGrid)" />
          <path
            d="M 20 0 L 0 0 0 20" fill="none" stroke-width="1"
          />
        </pattern>
        <filter id="blur">
          <feGaussianBlur in="SourceGraphic" stdDeviation="0,0" />
        </filter>
      </defs>

      <rect width="100%" height="100%" fill="url(#grid)" />

      <!-- Countries -->
      <g>
        <path
          v-for="p in countries" :key="p.id" :d="p.d"
          :fill="p.fill" class="country" data-type="country"
          @mouseover.stop="mouseover($event, p)" @mouseleave="mouseleave"
        />
      </g>

      <!-- Connections -->
      <g>
        <g
          v-for="(curve, index) in curves" :key="index"
          class="connection__head"
          :style="{
            offsetPath: `path('${curve.d}')`,
            animationDelay: index + 's',
            animationDuration: curve.duration
          }"
        >
          <circle
            r="3"
            fill="#697ebb"
          />
          <circle
            r="1.7"
            fill="#6ee4cf"
          />
        </g>

        <g>
          <path
            v-for="(curve, index) in curves" :key="index"
            class="connection" :d="curve.d" :style="{
              strokeDasharray: curve.length,
              strokeDashoffset: curve.length,
              animationDelay: index + 's',
              animationDuration: curve.duration
            }"
          />
        </g>
      </g>

      <g
        v-for="(point, key) in locations" :key="key" :count="point.count"
        class="location"
      >
        <circle
          :cy="point.d[1]" :cx="point.d[0]" :r="radius(point.count)"
          class="pulse" :style="{
            transformOrigin: `${point.d[0]}px ${point.d[1]}px`
          }"
        />
        <circle
          :cy="point.d[1]" :cx="point.d[0]" :r="radius(point.count)"
          class="pulse"
          :style="{
            transformOrigin: `${point.d[0]}px ${point.d[1]}px`,
            animationDelay: '1s'
          }"
        />
        <circle
          :cy="point.d[1]" :cx="point.d[0]" :r="radius(point.count)"
          class="pulse-border" :style="{
            transformOrigin: `${point.d[0]}px ${point.d[1]}px`
          }"
          data-type="location" @mouseover.stop="mouseover($event, point)" @mouseleave="mouseleave"
        />
        <circle
          :cy="point.d[1]" :cx="point.d[0]" :r="radius(point.count)"
          data-type="location" @mouseover.stop="mouseover($event, point)" @mouseleave="mouseleave"
        />
      </g>

      <g v-if="currentLocation" class="location location--current">
        <circle
          :cy="currentLocation.d[1]" :cx="currentLocation.d[0]" :r="currentLocation.r"
          class="pulse"
          :style="{
            transformOrigin: `${currentLocation.d[0]}px ${currentLocation.d[1]}px`
          }"
        />
        <circle
          :cy="currentLocation.d[1]" :cx="currentLocation.d[0]" :r="currentLocation.r"
          class="pulse"
          :style="{
            transformOrigin: `${currentLocation.d[0]}px ${currentLocation.d[1]}px`,
            animationDelay: '1s'
          }"
        />
        <circle
          :cy="currentLocation.d[1]" :cx="currentLocation.d[0]" :r="currentLocation.r"
          class="pulse-border"
          :style="{
            transformOrigin: `${currentLocation.d[0]}px ${currentLocation.d[1]}px`
          }"
        />
        <circle
          :cy="currentLocation.d[1]" :cx="currentLocation.d[0]" :r="currentLocation.r"
        />
      </g>
    </svg>

    <loading :active="loading" overlay />
  </div>
</template>
<script>
import { geoPath, geoMercator, scaleLinear, scaleThreshold } from 'd3'
import debounce from 'lodash.debounce'
import Loading from '@/components/Loading'
import { dotGet } from '@/utils'

export default {
  components: { Loading },
  props: {
    data: {
      type: Object,
      required: true
    },
    width: {
      type: Number,
      default: 961
    },
    height: {
      type: Number,
      default: 501
    },
    loading: {
      type: Boolean,
      default: false
    }
  },
  data () {
    return {
      world: {},
      tooltipCountry: null,
      tooltipLocation: null,
      tooltipPosition: null,
      containerWidth: 0,
      containerHeight: 0,
      zoomLevel: 1,
      resizeObserver: null
    }
  },
  computed: {
    actualWidth () {
      return this.containerWidth || this.width
    },
    actualHeight () {
      return this.containerHeight || this.height
    },
    aspectRatio () {
      return this.width / this.height
    },
    normalized () {
      const output = {}

      if (!this.data.field) {
        return {}
      }

      this.data.originalData.forEach(row => {
        if (!row || !row[this.data.field]) {
          return
        }

        const { count, locations } = row[this.data.field]
        const item = dotGet(row, this.data.rootField)
        let loaded = false

        locations.forEach(({ location, country }) => {
          const code = country.iso_code
          const coordinate = `${location.longitude},${location.latitude}`
          const defaultLocations = {
            [coordinate]: {
              ...location,
              count,
              items: [item]
            }
          }

          if (!output[code]) {
            output[code] = {
              locations: defaultLocations,
              count,
              items: [item]
            }
            loaded = true
            return
          }

          if (!output[code].locations) {
            output[code].locations = defaultLocations
            output[code].items = [item]
            loaded = true
            return
          }

          if (loaded) {
            return
          }

          loaded = true
          output[code].count += count

          output[code].items = output[code].items || []
          if (output[code].items.indexOf(item) === -1) {
            output[code].items.push(item)
          }

          if (output[code].locations[coordinate]) {
            output[code].locations[coordinate].count += count
            if (output[code].locations[coordinate].items === -1) {
              output[code].locations[coordinate].items.push(item)
            }

            return
          }

          output[code].locations[coordinate] = defaultLocations[coordinate]
        })
      })

      return output
    },
    fill () {
      return scaleThreshold()
        .domain(this.countryRange)
        .range(['#1b243c', '#232e4e', '#2b395f', '#334371', '#3b4d83', '#435894', '#4b62a6'])
    },
    countryCounts () {
      return Object.values(this.normalized).map(c => c.count).sort((a, b) => a - b)
    },
    countryRange () {
      const values = this.countryCounts
      if (!values.length) {
        return []
      }

      const upper = values[values.length - 1]
      const lower = values[0]
      const diff = upper - lower
      const range = []

      for (let i = 0; i < 6; i++) {
        range.push(lower + i * Math.floor(diff / 5))
      }

      return range
    },
    countries () {
      if (!this.world) {
        return {}
      }

      return (this.world.features || []).reduce((acc, feature) => {
        const item = this.normalized[feature.id]
        const count = item ? item.count : 0

        acc[feature.id] = {
          id: feature.id,
          d: this.pathFactory(feature),
          fill: this.currentLocation && feature.id === this.currentLocation.country.iso_code ? '#89afeb' : this.fill(count),
          name: feature.properties.name,
          count,
          marked: !!item
        }

        return acc
      }, {})
    },
    locations () {
      return Object.keys(this.normalized).reduce((acc, k) => {
        const val = this.normalized[k]

        if (!val || !val.locations) {
          return acc
        }

        Object.keys(val.locations).forEach(key => {
          const location = val.locations[key]
          if (!location) {
            return acc
          }

          if (acc[key]) {
            acc[key] += (parseInt(location.count) || 0)
            return acc
          }

          acc[key] = {
            ...location,
            d: this.projection([location.longitude, location.latitude]),
            normalized: val,
            country: this.countries[k]
          }
        })

        return acc
      }, {})
    },
    connections () {
      if (!this.currentLocation) {
        return []
      }

      const { location } = this.currentLocation
      if (!location) {
        return []
      }

      const connections = []

      for (var key in this.locations) {
        const point = this.locations[key]

        if (this.data.direction_reverse) {
          connections.push([
            this.projection([point.longitude, point.latitude]),
            this.projection([location.longitude, location.latitude])
          ])
          continue
        }

        connections.push([this.projection([location.longitude, location.latitude]), this.projection([point.longitude, point.latitude])])
      }

      return connections
    },
    curves () {
      return this.connections.map(connection => {
        const { distance, d } = this.curveFactory(connection)
        const speed = 1 / 12
        const x = document.createElementNS('http://www.w3.org/2000/svg', 'path')
        x.setAttributeNS(null, 'd', d)

        return {
          points: connection,
          d,
          distance,
          length: x.getTotalLength(),
          duration: (Math.round(distance) / speed) + 'ms'
        }
      })
    },
    $radius () {
      let top = 0
      if (this.countryCounts.length) {
        top = this.countryCounts[this.countryCounts.length - 1]
      }

      return scaleLinear().domain([0, top]).range([0, 6])
    },
    projection () {
      const width = this.actualWidth
      const height = this.actualHeight
      return geoMercator()
        .scale(width / 2.3 / Math.PI)
        .rotate([-11, 0])
        .translate([width / 2, height * 1.35 / 2])
    },
    pathFactory () {
      return geoPath().projection(this.projection)
    },
    curveFactory () {
      return (d) => {
        const from = d[0]
        const to = d[1]

        const dx = to[0] - from[0]
        const dy = to[1] - from[1]
        const dr = Math.sqrt(dx * dx + dy * dy)
        const diff = Math.random() * (1.5 - 2.1) + 2.1

        return {
          d: `M${from[0]},${from[1]} A${dr} ${dr * diff} 0 0 0 ${to[0]},${to[1]}`,
          distance: dr
        }
      }
    },
    currentLocation () {
      if (!this.data.currentLocation) {
        return
      }

      const { country, location } = this.data.currentLocation
      if (!location) {
        return
      }

      return {
        country,
        location,
        d: this.projection([location.longitude, location.latitude]),
        r: 3
      }
    }
  },
  mounted () {
    this.init()
    this.setupResponsive()
    this.detectZoomLevel()
    window.addEventListener('resize', this.handleResize)
  },
  beforeDestroy () {
    if (this.resizeObserver) {
      this.resizeObserver.disconnect()
    }
    window.removeEventListener('resize', this.handleResize)
  },
  methods: {
    setupResponsive () {
      if (!this.$refs.mapContainer) return

      // Use ResizeObserver to detect container size changes
      if (window.ResizeObserver) {
        this.resizeObserver = new ResizeObserver(this.handleContainerResize)
        this.resizeObserver.observe(this.$refs.mapContainer)
      }

      // Initial size calculation
      this.updateContainerDimensions()
    },
    handleContainerResize: debounce(function () {
      this.updateContainerDimensions()
      this.detectZoomLevel()
    }, 100),
    handleResize: debounce(function () {
      this.detectZoomLevel()
      this.updateContainerDimensions()
    }, 100),
    updateContainerDimensions () {
      if (!this.$refs.mapContainer) return

      const rect = this.$refs.mapContainer.getBoundingClientRect()
      const containerWidth = rect.width
      const containerHeight = rect.height

      // Maintain aspect ratio
      if (containerWidth / containerHeight > this.aspectRatio) {
        // Container is wider than needed, fit to height
        this.containerHeight = containerHeight
        this.containerWidth = containerHeight * this.aspectRatio
      } else {
        // Container is taller than needed, fit to width
        this.containerWidth = containerWidth
        this.containerHeight = containerWidth / this.aspectRatio
      }
    },
    detectZoomLevel () {
      // Detect browser zoom level using various methods
      const devicePixelRatio = window.devicePixelRatio || 1
      const screenWidth = screen.width
      const windowWidth = window.innerWidth

      // Calculate zoom level based on screen vs window width ratio
      let zoomLevel = 1

      if (window.outerWidth && window.innerWidth) {
        zoomLevel = window.outerWidth / window.innerWidth
      } else if (screenWidth && windowWidth) {
        zoomLevel = screenWidth / windowWidth / devicePixelRatio
      }

      // Clamp zoom level to reasonable bounds (0.5 to 2.0 for 50% to 200%)
      this.zoomLevel = Math.max(0.5, Math.min(2.0, zoomLevel))
    },
    mouseover: debounce(function ($event, item) {
      // Prevent interaction conflicts with customization mode
      if (this.isCustomizationMode()) {
        return
      }

      this.updatePosition($event)

      if ($event.target.dataset.type === 'country') {
        this.tooltipLocation = null
        this.tooltipCountry = item
        return
      }

      if ($event.target.dataset.type === 'location') {
        this.tooltipCountry = null
        this.tooltipLocation = item
      }
    }, 75, { maxWait: 500 }),
    mouseleave: debounce(function () {
      this.updatePosition()
    }, 75, { maxWait: 500 }),
    isCustomizationMode () {
      // Check if parent widget is in customization mode by looking for customization controls
      let parent = this.$parent
      while (parent) {
        if (parent.customizing !== undefined) {
          return parent.customizing
        }
        parent = parent.$parent
      }
      return false
    },
    updatePosition ($event) {
      if (!$event) {
        this.tooltipPosition = null
        return
      }

      const containerRect = this.$refs.mapContainer.getBoundingClientRect()
      const scrollX = window.scrollX || window.pageXOffset
      const scrollY = window.scrollY || window.pageYOffset

      // Calculate position relative to the map container
      let x = $event.pageX - (containerRect.left + scrollX)
      let y = $event.pageY - (containerRect.top + scrollY)

      // Adjust for any scaling or zoom effects
      const scaleX = containerRect.width / this.actualWidth
      const scaleY = containerRect.height / this.actualHeight

      // Use the smaller scale to maintain aspect ratio
      const scale = Math.min(scaleX, scaleY)

      // Adjust position based on actual scaling
      if (scale < 1) {
        x = x / scale
        y = y / scale
      }

      this.tooltipPosition = { x, y }
    },
    radius (x) {
      const r = this.$radius(x)
      if (r < 2) {
        return 2
      }

      return r
    },
    init () {
      const world = () => import('@/assets/world.json')
      world().then(data => {
        const world = data.default
        this.world = world
      })
    },
    // Method to be called by parent components when they need to resize
    resize () {
      this.$nextTick(() => {
        this.updateContainerDimensions()
        this.detectZoomLevel()
      })
    }
  }
}
</script>
<style lang="scss">
.world-map-container {
  position: relative;
  width: 100%;
  height: 100%;
  min-height: 200px;
  display: flex;
  align-items: center;
  justify-content: center;
  overflow: hidden;

  // Ensure container doesn't interfere with customization controls
  .box--widget.box--moveable & {
    // Add slight margin when in customization mode to prevent overlap
    margin-bottom: 2px;
  }
}

.world-map {
  background-color: #1f2128;
  max-width: 100%;
  max-height: 100%;
  display: block;

  // Ensure the SVG scales properly with browser zoom
  @media screen and (min-width: 1px) {
    transform-origin: center center;
  }
}

.country {
  opacity: 0.8;
  stroke: #697ebb;
  stroke-width: 0.6;
  transition: fill 150ms linear;
  &:hover {
    fill: #576fb3;
    opacity: 1;
  }
}

.pattern {
  path {
    stroke: #2a2d36;
  }
}

.connection {
  fill: none;
  stroke: #6ee4cf;
  animation: draw infinite ease-in;
}

.connection__head {
  animation: pathy infinite ease-in;
  offset-distance: 0;
}

.location {
  circle {
    fill: #ee63a0;
    &:hover {
      cursor: pointer;
    }
  }

  circle.pulse-border {
    fill: none;
    stroke: #ee63a0;
    transform-origin: center;
    animation: pulse 3s cubic-bezier(0.39, 0.54, 0.41, 1.5) infinite;
    opacity: 1;
  }

  circle.pulse {
    animation: pulse-me 2s linear infinite;
    fill: #ee63a0;
  }

  &.location--current {
    circle {
      fill: #fac56d;
    }

    circle:not(:last-child) {
      fill: none;
      stroke: #fac56d;
    }
  }
}

.tooltip {
  color: #222;
  background: #fff;
  border-radius: 3px;
  padding: .5em;
  opacity: 0.9;
  position: absolute;
  font-size: 0.8rem;
  z-index: 1000;
  pointer-events: none;
  white-space: nowrap;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.15);

  // Ensure tooltip remains readable at different zoom levels
  min-width: 120px;
  max-width: 300px;

  // Prevent tooltip from being affected by parent transforms
  will-change: transform;

  // Ensure tooltip stays above other elements including customization controls
  z-index: 10000;
}

.tooltip__title {
  font-weight: bold;
  font-size: 1rem;
}

.tooltip__subtitle {
  padding-bottom: .4em;
  margin-bottom: .4em;
  border-bottom: 1px solid #ddd;
}

@keyframes pathy {
  100% {
    offset-distance: 100%;
  }
}

@keyframes draw {
  0% {
    stroke-opacity: 0.5;
    stroke-width: 0.1;
  }

  50% {
    stroke-opacity: 1;
    stroke-width: 1;
  }

  100% {
    stroke-dashoffset: 0;
    stroke-opacity: 0.5;
    stroke-width: 0.5;
  }
}

@keyframes pulse {
  0% {
    transform: scale(0.3);
    stroke-width: 3px;
    stroke-opacity: 1;
  }
  100% {
    transform: scale(2.5);
    stroke-width: 0;
    stroke-opacity: 0;
  }
}

@keyframes pulse-me {
  0% {
    transform: scale(0.5);
    opacity: 0;
  }
  50% {
    opacity: 0.2;
  }
  70% {
    opacity: 0.1;
  }
  100% {
    transform: scale(3);
    opacity: 0;
  }
}
</style>
